/**
 * ThemeSwitcher.js - UI component for switching themes
 * 
 * A dropdown or button group that allows users to switch themes with one click.
 * Can be placed anywhere in the UI (header, settings panel, etc.)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Button, Icon } from 'semantic-ui-react';
import {
    applyTheme,
    getCurrentTheme,
    isDarkMode,
    getAllThemes,
    // aliased: the components below declare a local `themes` from getAllThemes()
    themes as themePalettes
} from './ThemeManager';

/**
 * Swatch for one theme: the panel the lab chrome actually becomes (--sidebar) on one
 * half, the accent that identifies the theme (--primary) on the other.
 *
 * --primary alone was misleading about the thing users most want to see - whether a
 * theme is light or dark. Espresso's primary is a light caramel although espresso is
 * a dark theme; slate's is dark although slate is light. Showing the panel alongside
 * it makes that immediately readable while keeping the hue that tells themes apart.
 *
 * Derived from the palettes rather than duplicated, so a swatch can no longer drift
 * from the theme it represents - snow's hardcoded copy had already gone stale
 * (L 0.95 against a real --primary of L 0.55).
 */
export const Swatch = ({ themeId, size, idSuffix = '' }) => {
    const colors = themePalettes[themeId]?.colors;
    if (!colors) return null;

    // unique per render site: the current theme is drawn both on the trigger and in the grid
    const gradientId = `theme-swatch-${themeId}${idSuffix}`;

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            aria-hidden="true"
            focusable="false"
            // width/height attributes alone are not enough: these sit inside flex
            // containers, where the SVG gets shrunk to its min-content width
            style={{ display: 'block', width: size, height: size, flexShrink: 0 }}
        >
            <defs>
                <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
                    {/* a hair of ramp rather than a hard stop, so the diagonal does not stair-step */}
                    <stop offset="49.5%" stopColor={colors['--sidebar']} />
                    <stop offset="50.5%" stopColor={colors['--primary']} />
                </linearGradient>
            </defs>
            <circle
                cx="16"
                cy="16"
                r="15.25"
                fill={`url(#${gradientId})`}
                stroke="rgba(0,0,0,0.14)"
                strokeWidth="1"
            />
        </svg>
    );
};

// Theme icons mapping
const themeIcons = {
    slate: '👓',
    earthy: '🌍',
    ocean: '🌊',
    forest: '🌲',
    midnight: '🌑',
    blossom: '🌸',
    snow: '❄️',
    espresso: '☕',
    freshOrange: '🍊'
};


/**
 * Compact icon-only theme dropdown - saves space while showing all themes
 * Shows current theme color as button, expands to show themes as color circles
 */
export const ThemeDropdown = ({ style = {} }) => {
    const [currentTheme, setCurrentTheme] = useState(getCurrentTheme());
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const themes = getAllThemes();

    // Listen for theme changes from other sources
    useEffect(() => {
        const handleThemeChange = (e) => {
            setCurrentTheme(e.detail.theme);
        };
        window.addEventListener('themechange', handleThemeChange);
        return () => window.removeEventListener('themechange', handleThemeChange);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleThemeSelect = (themeId) => {
        // Only act if selecting a different theme
        if (themeId !== currentTheme) {
            // Apply live: applyTheme persists to localStorage, sets the CSS custom
            // properties, notifies the Haxe lab via the setTheme bridge and dispatches
            // the 'themechange' event (which updates currentTheme above).
            applyTheme(themeId);
            setIsOpen(false);
        } else {
            // Same theme selected, just close the dropdown
            setIsOpen(false);
        }
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', ...style }}>
            {/* Trigger Button - shows current theme color */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                title={`Current Theme: ${themes.find(t => t.id === currentTheme)?.name}`}
                style={{
                    width: '32px',
                    height: '32px',
                    minWidth: '32px',
                    minHeight: '32px',
                    padding: 0,
                    aspectRatio: '1',
                    flexShrink: 0,
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)',
                    backgroundColor: 'var(--card)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    boxShadow: isOpen ? 'var(--shadow-md)' : 'none',
                    position: 'relative'
                }}
            >
                <div style={{
                    position: 'relative',
                    width: '20px',
                    height: '20px',
                    flexShrink: 0
                }}>
                    <Swatch themeId={currentTheme} size={20} idSuffix="-trigger" />
                    <span style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        pointerEvents: 'none'
                    }}>
                        {themeIcons[currentTheme]}
                    </span>
                </div>
            </button>

            {/* Dropdown Menu - Circles only */}
            {
                isOpen && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '6px',
                            backgroundColor: 'var(--card)',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius)',
                            boxShadow: 'var(--shadow-lg)',
                            padding: '12px',
                            width: '136px',
                            zIndex: 10000,
                            animation: 'fadeIn 0.15s ease'
                        }}
                    >
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '12px',
                            justifyItems: 'center'
                        }}>
                            {themes.map(theme => (
                                <button
                                    key={theme.id}
                                    onClick={() => handleThemeSelect(theme.id)}
                                    title={theme.name}
                                    style={{
                                        width: '28px',
                                        height: '28px',
                                        minWidth: '28px',
                                        minHeight: '28px',
                                        padding: 0,
                                        aspectRatio: '1',
                                        flexShrink: 0,
                                        // the swatch draws its own circle and stroke; a CSS
                                        // border here would clip the SVG and re-introduce the
                                        // jagged edge, so selection is shown with a ring only
                                        border: 'none',
                                        background: 'transparent',
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                        transition: 'transform 0.1s ease',
                                        boxShadow: currentTheme === theme.id
                                            ? '0 0 0 2px var(--card), 0 0 0 4px var(--primary)'
                                            : 'none',
                                        transform: currentTheme === theme.id ? 'scale(1.1)' : 'scale(1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (currentTheme !== theme.id) e.currentTarget.style.transform = 'scale(1.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (currentTheme !== theme.id) e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                >
                                    <Swatch themeId={theme.id} size={28} />
                                </button>
                            ))}
                        </div>
                    </div>
                )
            }

            {/* Inline keyframe animation */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div >
    );
};

/**
 * Compact theme switcher with dropdown (Semantic UI based)
 */
export const ThemeSwitcher = ({ style = {} }) => {
    const [currentTheme, setCurrentTheme] = useState(getCurrentTheme());
    const themes = getAllThemes();

    // Listen for theme changes from other sources
    useEffect(() => {
        const handleThemeChange = (e) => {
            setCurrentTheme(e.detail.theme);
        };
        window.addEventListener('themechange', handleThemeChange);
        return () => window.removeEventListener('themechange', handleThemeChange);
    }, []);

    const handleThemeChange = (e, { value }) => {
        applyTheme(value);
        setCurrentTheme(value);
    };

    const themeOptions = themes.map(theme => ({
        key: theme.id,
        value: theme.id,
        text: theme.name,
        description: theme.description
    }));

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...style }}>
            <Dropdown
                selection
                compact
                options={themeOptions}
                value={currentTheme}
                onChange={handleThemeChange}
                style={{
                    minWidth: '130px',
                    backgroundColor: 'var(--card)',
                    color: 'var(--card-foreground)',
                    border: '1px solid var(--border)'
                }}
            />
        </div>
    );
};

/**
 * Inline theme buttons - shows all themes as color swatches
 */
export const ThemeSwatches = ({ size = 'small', showLabels = false, style = {} }) => {
    const [currentTheme, setCurrentTheme] = useState(getCurrentTheme());
    const themes = getAllThemes();

    useEffect(() => {
        const handleThemeChange = (e) => {
            setCurrentTheme(e.detail.theme);
        };
        window.addEventListener('themechange', handleThemeChange);
        return () => window.removeEventListener('themechange', handleThemeChange);
    }, []);
    // Primary colors for each theme (for swatch preview)
    // Primary colors for each theme (for swatch preview)
    const swatchColors = {
        slate: 'oklch(0.45 0.04 260)',
        earthy: 'oklch(0.6180 0.0778 65.5444)',
        ocean: 'oklch(0.55 0.15 240)',
        forest: 'oklch(0.50 0.12 155)',
        midnight: 'oklch(0.50 0.18 280)',
        blossom: 'oklch(0.65 0.18 350)',
        snow: 'oklch(0.95 0.02 220)',
        espresso: 'oklch(0.65 0.12 60)',
        freshOrange: 'oklch(0.65 0.20 45)'
    };
    const handleClick = (themeId) => {
        applyTheme(themeId);
        setCurrentTheme(themeId);
    };

    const swatchSize = size === 'small' ? 24 : size === 'medium' ? 32 : 40;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', ...style }}>
            {themes.map(theme => (
                <button
                    key={theme.id}
                    onClick={() => handleClick(theme.id)}
                    title={`${theme.name}: ${theme.description}`}
                    style={{
                        width: swatchSize,
                        height: swatchSize,
                        borderRadius: '50%',
                        border: currentTheme === theme.id
                            ? '3px solid var(--primary)'
                            : '2px solid var(--border)',
                        backgroundColor: swatchColors[theme.id],
                        cursor: 'pointer',
                        transition: 'transform 0.15s ease, border-color 0.15s ease',
                        transform: currentTheme === theme.id ? 'scale(1.15)' : 'scale(1)',
                        boxShadow: currentTheme === theme.id
                            ? '0 2px 8px rgba(0,0,0,0.2)'
                            : 'none'
                    }}
                >
                    {showLabels && (
                        <span style={{
                            position: 'absolute',
                            bottom: -18,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '10px',
                            whiteSpace: 'nowrap'
                        }}>
                            {theme.name}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
};

/**
 * Simple icon button that cycles through themes
 */
export const ThemeCycleButton = ({ style = {} }) => {
    const [currentTheme, setCurrentTheme] = useState(getCurrentTheme());
    const themeNames = ['slate', 'espresso', 'earthy', 'ocean', 'forest', 'midnight', 'blossom', 'snow', 'freshOrange'];

    useEffect(() => {
        const handleThemeChange = (e) => {
            setCurrentTheme(e.detail.theme);
        };
        window.addEventListener('themechange', handleThemeChange);
        return () => window.removeEventListener('themechange', handleThemeChange);
    }, []);

    const cycleTheme = () => {
        const currentIndex = themeNames.indexOf(currentTheme);
        const nextIndex = (currentIndex + 1) % themeNames.length;
        const nextTheme = themeNames[nextIndex];
        applyTheme(nextTheme);
        setCurrentTheme(nextTheme);
    };

    return (
        <Button
            icon
            size="small"
            onClick={cycleTheme}
            title={`Current: ${currentTheme} (click to cycle)`}
            style={{
                backgroundColor: 'var(--secondary)',
                color: 'var(--secondary-foreground)',
                ...style
            }}
        >
            <Icon name="adjust" />
        </Button>
    );
};

export default ThemeDropdown;
